// E3 — DRIVER (app layer). Walks the PURE self-repair machine over two injected
// PORTS — `check` (wraps the verify checker) and `runAgent` (wraps a bounded
// `runLocalAgentLoop` sub-run). It is the orchestrator; it decides NOTHING about
// state transitions itself (that's the machine) and runs NOTHING concrete
// (those are the ports). Mirrors `fanOutAndMerge`: a driver over the loop, so it
// NEVER touches `agentLoop.js` or the frozen core.
//
// Dependency Rule (Tier 1): this file imports ONLY the domain machine + the
// prompt builders. `check`/`runAgent` arrive as arguments — the composition root
// (SessionWorkspace) wires the concretions. No React, no Electron, no loop import.
//
// Guarantees: BOUNDED (the machine caps cycles + a hard safety cap on total
// transitions), READ-ONLY-SAFE (skips when the turn touched no code), honors an
// AbortSignal (→ ESCALATED), and NEVER throws (every port call is wrapped; on
// error the result is treated as a failed cycle with the error as evidence).

import { initialRepair, transition } from './selfRepairMachine.js';
import { buildFixPrompt, buildTroubleshootPrompt } from './prompts.js';

const noop = () => {};

function isAborted(signal) {
  return !!(signal && signal.aborted);
}

// Fire the UI/event sink without ever letting a listener throw escape the driver.
function emit(onEvent, payload) {
  try {
    onEvent(payload);
  } catch {
    // a misbehaving listener must not crash a self-repair turn.
  }
}

// Run a port that returns a Promise, but never reject: map a throw to `onError`.
async function safeCall(fn, onError) {
  try {
    return await fn();
  } catch (err) {
    return onError(err);
  }
}

const errText = (err) => (err && err.message ? err.message : String(err));

/**
 * Drive the self-repair machine after a Code turn.
 *
 * @param {object}   args
 * @param {object}   args.turnResult           { finalText, touchedCode } — the just-finished turn.
 * @param {string}   args.root                 project root passed through to the check port.
 * @param {Function} args.runAgent             ({ task, mode:'fix'|'troubleshoot', signal }) => Promise<{ ok, finalText }>. PORT.
 * @param {Function} args.check                ({ root, signal }) => Promise<{ passed, evidence, cmd }>. PORT.
 * @param {number}   [args.blockedThreshold=2] failed repair cycles before ESCALATE (the machine's cap).
 * @param {Function} [args.onEvent]            ({ type:'self_repair', state, cycle, evidence? }) per transition.
 * @param {AbortSignal} [args.signal]          abort → ESCALATED.
 * @returns {Promise<{ ran:boolean, finalState:string, cycles:number, escalated:boolean, assurance:boolean, report:object|null }>}
 *          Always RESOLVES — never rejects.
 */
export async function selfRepairLoop({
  turnResult,
  root,
  runAgent,
  check,
  blockedThreshold = 2,
  onEvent = noop,
  signal,
} = {}) {
  // READ-ONLY-SAFE: nothing to repair if the turn touched no code. Skip — no
  // check, no sub-run, no events.
  if (!turnResult || !turnResult.touchedCode) {
    return { ran: false, finalState: null, cycles: 0, escalated: false, assurance: false, report: null };
  }

  let ctx = initialRepair({ blockedThreshold });

  // Latest failure evidence (threaded into prompts + the escalate report) and
  // the set of agent stages we actually ran (for the report's `tried`).
  let lastEvidence = '';
  const tried = [];

  // BOUNDING — belt & suspenders. The machine already caps cycles; this hard cap
  // on TOTAL transitions guarantees termination even against a pathological
  // machine/port. Each blocked cycle is ~4 transitions; +5 covers the head/tail.
  const maxTransitions = ctx.blockedThreshold * 4 + 5;
  let transitions = 0;

  // Apply one event, fire onEvent, advance ctx. Returns the transition result.
  const advance = (event) => {
    const next = transition(ctx, event);
    ctx = next;
    emit(onEvent, { type: 'self_repair', state: next.state, cycle: next.cycles, evidence: lastEvidence || undefined });
    return next;
  };

  // Kick off: PROPOSED → CHECKING (action run_check).
  let result = advance({ type: 'START' });

  while (!result.terminal && transitions < maxTransitions) {
    transitions += 1;

    // Honor abort at the TOP of every step — before dispatching the next port.
    // (A signal that fired DURING the previous port call is caught here, so the
    // pending action — e.g. a fixer sub-run — never starts.)
    if (isAborted(signal)) {
      result = advance({ type: 'ABORT' });
      break;
    }

    const { action } = result;

    if (action === 'run_check') {
      const res = await safeCall(
        () => check({ root, signal }),
        (err) => ({ passed: false, evidence: errText(err), cmd: null }),
      );
      lastEvidence = res && res.evidence ? String(res.evidence) : lastEvidence;
      // AMBIENTE ausente (o check NÃO PÔDE rodar — ex.: worktree sem node_modules,
      // "command not found"/exit 127). Isso não é regressão de código: PULA o reparo
      // em silêncio (nem "passou" nem escala) — sem needs_planning falso a cada turno.
      if (res && res.notRunnable) {
        emit(onEvent, { type: 'self_repair', state: 'SKIPPED', cycle: ctx.cycles, evidence: lastEvidence || undefined });
        return { ran: false, finalState: 'SKIPPED', cycles: ctx.cycles, escalated: false, assurance: false, report: null, skippedReason: 'env' };
      }
      result = advance({ type: 'CHECK_RESULT', passed: !!(res && res.passed) });
    } else if (action === 'run_fixer' || action === 'run_troubleshooter') {
      const mode = action === 'run_fixer' ? 'fix' : 'troubleshoot';
      const task =
        mode === 'fix'
          ? buildFixPrompt(lastEvidence)
          : buildTroubleshootPrompt(lastEvidence, 'o fixer rodou e a verificação seguiu vermelha');
      tried.push(mode);
      const res = await safeCall(
        () => runAgent({ task, mode, signal }),
        (err) => ({ ok: false, finalText: errText(err) }),
      );
      result = advance({ type: 'AGENT_DONE', ok: !!(res && res.ok) });
    } else {
      // No action to run but not terminal: nothing the driver can do — stop to
      // stay bounded (defensive; the contract's machine never yields this).
      break;
    }
  }

  const finalState = ctx.state;
  const escalated = !!result.escalated;
  const assurance = finalState === 'PASS';

  const report = escalated
    ? { tried: [...tried], lastEvidence, cycles: ctx.cycles, recommendation: 'needs_planning' }
    : null;

  return { ran: true, finalState, cycles: ctx.cycles, escalated, assurance, report };
}
